/**
 * The bank texts left for the owner to name (TMMAPP-0164 item 12; TMMAPI-0195 U3): one group per
 * naming key, exactly the texts `naming-status.bySource.UNNAMED` counts (`kept: true`: the ones kept
 * unnamed, `KEPT_UNNAMED`). Naming a group names every line with its key on all the owner's accounts
 * and writes the alias; keeping one takes it out of the list and every naming run until a newer line
 * with its key arrives. Owner only: anything else is `404 PAYEE_TEXT_NOT_FOUND`, as is a key with
 * nothing left. Wire types from `src/api/generated/`.
 */
import { request } from './client';
import type { PayeeKind, PayeeRef } from './payees';
import type {
  UnnamedPayeeKeyRequest,
  UnnamedPayeeNameRequest,
  UnnamedPayeeNameResponse as WireNameResponse,
  UnnamedPayeeText as WireGroup,
  UnnamedPayeeTexts as WirePage,
} from './generated';

/** The page size the section reads (the API's default; at most 200). */
export const UNNAMED_PAGE_SIZE = 50;

/** The group's newest line: its date, amount (in its own currency) and account. */
export interface UnnamedPayeeSample {
  accountId: number | null;
  transactionId: number | null;
  postedOn: string | null;
  /** Signed, in `currency` (the line's bank currency). */
  amountMinor: number | null;
  currency: string | null;
  accountName: string | null;
}

export interface UnnamedPayeeGroup {
  /** The naming key: what name, keep and restore send back. */
  key: string;
  /** The newest line's text, as imported. */
  bankText: string;
  lineCount: number;
  firstOn: string;
  lastOn: string;
  sample: UnnamedPayeeSample;
  /** AI looked at this text and had nothing. */
  aiEvaluated: boolean;
  /** A standing proposal's name; null when there is none. */
  suggestedName: string | null;
  /** `PERSON` over a person-to-person rail; null when not served. */
  kindHint: PayeeKind | null;
}

export interface UnnamedPayeePage {
  groups: UnnamedPayeeGroup[];
  total: number;
  nextCursor: string | null;
}

export interface UnnamedPayeeNamed {
  payee: PayeeRef | null;
  linesNamed: number;
  linesSkipped: number;
}

/** Exactly one of an existing payee or a new name; `kind` only with a new name. */
export type UnnamedPayeeNameBody =
  { key: string; payeeId: number } | { key: string; name: string; kind: PayeeKind };

function normalizeGroup(w: WireGroup): UnnamedPayeeGroup {
  return {
    key: w.key,
    bankText: w.bankText,
    lineCount: w.lineCount,
    firstOn: w.firstOn,
    lastOn: w.lastOn,
    sample: {
      accountId: w.sample.accountId ?? null,
      transactionId: w.sample.transactionId ?? null,
      postedOn: w.sample.postedOn ?? null,
      amountMinor: w.sample.amountMinor ?? null,
      currency: w.sample.currency ?? null,
      accountName:
        w.sample.accountName != null && w.sample.accountName.length > 0
          ? w.sample.accountName
          : null,
    },
    aiEvaluated: w.aiEvaluated,
    suggestedName:
      w.suggestedName != null && w.suggestedName.trim().length > 0 ? w.suggestedName : null,
    kindHint: w.kindHint ?? null,
  };
}

export function normalizeUnnamedPage(w: WirePage): UnnamedPayeePage {
  const groups = (w.groups ?? []).map(normalizeGroup);
  return {
    groups,
    total: w.total ?? groups.length,
    nextCursor: w.nextCursor != null && w.nextCursor.length > 0 ? w.nextCursor : null,
  };
}

export const unnamedPayeesApi = {
  /** GET /v1/me/payees/unnamed: a page, most lines first. */
  async list(
    opts: { kept?: boolean; cursor?: string | null; limit?: number } = {},
  ): Promise<UnnamedPayeePage> {
    const query: Record<string, string | number | boolean> = {
      limit: opts.limit ?? UNNAMED_PAGE_SIZE,
    };
    if (opts.kept === true) query.kept = true;
    if (opts.cursor != null) query.cursor = opts.cursor;
    return normalizeUnnamedPage(await request<WirePage>('GET', '/v1/me/payees/unnamed', { query }));
  },

  /** POST /v1/me/payees/unnamed/name: names every line with the key and writes the alias. */
  async name(body: UnnamedPayeeNameBody): Promise<UnnamedPayeeNamed> {
    const json: UnnamedPayeeNameRequest =
      'payeeId' in body
        ? { key: body.key, payeeId: body.payeeId }
        : { key: body.key, name: body.name, kind: body.kind };
    const w = await request<WireNameResponse>('POST', '/v1/me/payees/unnamed/name', { json });
    return {
      payee: w.payee ?? null,
      linesNamed: w.linesNamed ?? 0,
      linesSkipped: w.linesSkipped ?? 0,
    };
  },

  /** POST /v1/me/payees/unnamed/keep: out of the list and every naming run (safe to repeat). */
  async keep(key: string): Promise<void> {
    const json: UnnamedPayeeKeyRequest = { key };
    await request<void>('POST', '/v1/me/payees/unnamed/keep', { json });
  },

  /** POST /v1/me/payees/unnamed/restore: back on the list (safe to repeat). */
  async restore(key: string): Promise<void> {
    const json: UnnamedPayeeKeyRequest = { key };
    await request<void>('POST', '/v1/me/payees/unnamed/restore', { json });
  },
};
